import React, { FunctionComponent, useState } from "react";

/* Validation */
import { Formik, useFormikContext } from "formik";
import validationSchema, { FormData, SkadeType } from "./formConfig";

/* API */
import axios from "axios";
import { Endpoints } from "@internal/common";
import {
  Alert,
  Button,
  Flex,
  Label,
  Text,
  Textarea,
  Title,
  ValidationMessage,
} from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";
import FormInputGroup from "./components/FormInputGroup";

const WritetoUsPrivate = () => {
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>();

  const sendFormData = (formData: FormData) => {
    setApiError(false);
    setIsLoading(true);
    setFormData(formData);
    axios
      .post(Endpoints.WriteToUsPrivate, formData)
      .then(() => setSubmitted(true))
      .catch(() => {
        setApiError(true);
        setIsLoading(true);
      });
  };

  const DEFAULT_SUCCESS_MSG =
    "Vi kontakter dig indenfor 1-3 arbejdsdage. Hvis det haster, er du velkommen til at ringe til os på telefonen 70 10 90 09";
  const SALES_SUCCESS_MSG =
    "Vi kontakter dig indenfor 1 arbejdsdag. Hvis det haster, er du velkommen til at ringe til os på telefonen 70 10 90 09";

  return (
    <Flex center={"md"} fill layout={1}>
      {submitted ? (
        <Alert severity="success" tracking={{eventLabel: 'tak-for-din-henvendelse'}} style={{ whiteSpace: "pre-line" }}>
          <Title>Tak for din henvendelse</Title>
          <Text>
            {formData?.alreadyCustomer === "Nej" &&
            formData?.subject === "Tilbud på en ny privatforsikring"
              ? SALES_SUCCESS_MSG
              : DEFAULT_SUCCESS_MSG}
          </Text>
        </Alert>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            name: "",
            email: "",
            phoneNumber: "",
            customerNumber: "",
            subject: "",
            alreadyCustomer: "",
            skadetype: "",
            content: "",
            department: "",
          }}
          validationSchema={validationSchema()}
          onSubmit={(formData: FormData) => {
            sendFormData(formData);
          }}
        >
          <RenderForm isLoading={isLoading} />
        </Formik>
      )}
      {apiError && (
        <Alert severity="error" style={{ whiteSpace: "pre-line" }} tracking={{eventLabel: 'der-skete-en-fejl'}}>
          <Title>Der skete en fejl.</Title>
          <Text>
            Der skete en fejl ved indsendelse. Prøv venligst igen senere
          </Text>
        </Alert>
      )}
    </Flex>
  );
};

interface RenderProps {
  isLoading: boolean;
}

const RenderForm: FunctionComponent<RenderProps> = () => {
  const { handleSubmit, handleBlur, setFieldValue, touched, errors, values } =
    useFormikContext<FormData>();

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="name"
          labelText="Navn *"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("name", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.name && errors.name)}
          errorText={errors.name}
          value={values.name}
        />
        <FormInput
          id="email"
          labelText="Din mail *"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={values.email}
        />
        <FormInput
          id="phoneNumber"
          labelText="Telefonnummer"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("phoneNumber", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.phoneNumber && errors.phoneNumber)}
          errorText={errors.phoneNumber}
          value={values.phoneNumber}
        />
        <FormInputGroup
          labelText="Er du allerede kunde hos os? *"
          id="alreadyCustomer"
          onChange={(event) => {
            setFieldValue("alreadyCustomer", event.at(0));
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={values.alreadyCustomer}
          onBlur={handleBlur}
          invalid={!!(touched.alreadyCustomer && errors.alreadyCustomer)}
          errorText={errors.alreadyCustomer}
        />
        {values.alreadyCustomer === "Ja" ? (
          <FormInputGroup
            labelText="Hvad drejer din henvendelse sig om? *"
            id="subject"
            onChange={(event) => {
              setFieldValue("subject", event.at(0));
            }}
            options={[
              {
                label: "Sundhedsforsikring person",
                value: "Sundhedsforsikring person",
              },
              {
                label: "Skade eller brug af sundhedsforsikringen",
                value: "Skade",
              },
              {
                label: "Opsigelse af forsikring",
                value: "Opsigelse af forsikring",
              },
              {
                label: "Mine private forsikringer",
                value: "Mine private forsikringer",
              },
              {
                label: "Erhvervsforsikringer",
                value: "Erhvervsforsikringer",
              },
              {
                label: "Landbrugsforsikringer",
                value: "Landbrugsforsikringer",
              },
            ]}
            type="radio"
            value={values.subject}
            onBlur={handleBlur}
            invalid={!!(touched.subject && errors.subject)}
            errorText={errors.subject}
          />
        ) : values.alreadyCustomer === "Nej" ? (
          <FormInputGroup
            labelText="Hvad drejer din henvendelse sig om? *"
            id="subject"
            onChange={(event) => {
              setFieldValue("subject", event.at(0));
            }}
            options={[
              {
                label: "Tilbud på en ny privatforsikring",
                value: "Tilbud på en ny privatforsikring",
              },
              {
                label: "Henvendelse på vegne af anden kunde",
                value: "Henvendelse på vegne af anden kunde",
              },
              {
                label: "Spørgsmål til en skadessag",
                value: "Skade",
              },
              {
                label: "Andre henvendelser",
                value: "Andre henvendelser",
              },
            ]}
            type="radio"
            value={values.subject}
            onBlur={handleBlur}
            invalid={!!(touched.subject && errors.subject)}
            errorText={errors.subject}
          />
        ) : null}
        {values.subject === "Skade" && (
          <FormInputGroup
            labelText="Hvilken skade gælder det? *"
            id="skadetype"
            onChange={(event) => {
              setFieldValue("skadetype", event.at(0));
            }}
            options={Object.entries(SkadeType).map((key) => {
              return { label: key[1], value: key[1] };
            })}
            type="radio"
            value={values.skadetype}
            onBlur={handleBlur}
            invalid={!!(touched.skadetype && errors.skadetype)}
            errorText={errors.skadetype}
          />
        )}
        {values.alreadyCustomer === "Ja" && (
          <FormInput
            id="customerNumber"
            labelText={
              values.subject === "Sundhedsforsikring"
                ? "Dit CPR- eller skadenummer *"
                : "Dit kunde-, police- eller skadenummer *"
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("customerNumber", event.target.value);
            }}
            onBlur={handleBlur}
            invalid={!!(touched.customerNumber && errors.customerNumber)}
            errorText={errors.customerNumber}
            value={values.customerNumber}
          />
        )}
        <div>
          <Label htmlFor={"content-textarea"}>
            Hvad kan vi hjælpe dig med? *
          </Label>
          <Textarea
            id="content-textarea"
            maxLength={500}
            name="content"
            value={values.content}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              setFieldValue("content", event.target.value);
            }}
            onBlur={handleBlur}
            invalid={!!(touched.content && errors.content)}
          />
          <ValidationMessage
            htmlFor={"content-textarea"}
            hidden={!(touched.content && errors.content)}
            invalid={!!(touched.content && errors.content)}
          >
            {errors.content}
          </ValidationMessage>
        </div>
        <Button type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default WritetoUsPrivate;
